/**
 * La evaluación de inglés por llamada, del lado del cliente: tipos del contrato
 * (job-apply-service `lib/englishAssessment.ts`), tonos de estado y el armado
 * del número de WhatsApp en E.164. Lo comparten la consola de agencia, el panel
 * del candidato y la operación.
 */
import type { ToneName } from '~/components/ui';

export type EnglishAssessmentStatus =
  | 'requested'
  | 'notified'
  | 'in_call'
  | 'scoring'
  | 'completed'
  | 'expired'
  | 'canceled'
  | 'failed';

export type CefrLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';
export type ScoreDimension = 'fluency' | 'grammar' | 'vocabulary' | 'comprehension' | 'pronunciation';

export interface EnglishResult {
  cefr: CefrLevel | null;
  insufficient_evidence: boolean;
  scores: Record<ScoreDimension, number | null>;
  summary_es: string;
  summary_en: string;
  evidence: Array<{ quote: string; dimension: ScoreDimension; note?: string }>;
  confidence: 'low' | 'medium' | 'high';
  scored_at: string;
  model?: string;
  rubric_version?: string;
  /** v2: la pronunciación desde el audio de la llamada. null = no se pudo medir. */
  pronunciation?: PronunciationDetail | null;
  pronunciation_reason?: string;
}

export interface PronunciationDetail {
  score: number;
  cefr: CefrLevel;
  intelligibility: number | null;
  sounds: number | null;
  prosody: number | null;
  issues: Array<{ at: string; target: string; note_es?: string }>;
  summary_es: string;
  summary_en: string;
  confidence: 'low' | 'medium' | 'high';
}

/** Si hay grabación para escuchar. La URL se pide aparte, firmada y corta. */
export interface RecordingSummary {
  available: boolean;
  duration_secs: number | null;
  expires_at: string | null;
}

/** Lo que devuelve el endpoint de la grabación (agencia o candidato). */
export interface RecordingUrl {
  url: string;
  expires_in: number;
  content_type: string;
  duration_secs: number | null;
}

export interface TranscriptTurn {
  role: 'agent' | 'candidate';
  text: string;
  t_secs?: number;
}

export interface AssessmentCall {
  started_at: string | null;
  ended_at: string | null;
  duration_secs: number | null;
  candidate_speech_secs: number | null;
  verdict: 'scored' | 'insufficient' | 'lost' | null;
}

/** Lo común a la vista de agencia y la del candidato. */
export interface EnglishAssessmentBase {
  _id: string;
  status: EnglishAssessmentStatus;
  requested_at: string | null;
  expires_at: string | null;
  calls: AssessmentCall[];
  calls_left: number;
  result: EnglishResult | null;
  transcript: TranscriptTurn[];
  recording?: RecordingSummary;
  created_at: string | null;
}

export interface AgencyEnglishAssessment extends EnglishAssessmentBase {
  match_id: string;
  posting_id: string;
  result_source: 'reused' | 'shared_call' | null;
  notified_by?: { email: boolean; whatsapp: boolean };
}

export interface CandidateEnglishAssessment extends EnglishAssessmentBase {
  agency: { name: string | null };
  posting: { title: string | null; company: string | null };
  phone_e164: string | null;
}

export interface CallLine {
  call_link: string;
  chat_link: string;
  display: string;
}

export const ENGLISH_STATUS_TONES: Record<EnglishAssessmentStatus, ToneName> = {
  requested: 'warn',
  notified: 'warn',
  in_call: 'info',
  scoring: 'info',
  completed: 'good',
  expired: 'muted',
  canceled: 'muted',
  failed: 'bad',
};

/** Los que esperan algo del candidato o del sistema. */
export const ACTIVE_STATUSES: EnglishAssessmentStatus[] = ['requested', 'notified', 'in_call', 'scoring'];
/** Los que la agencia puede cancelar. */
export const CANCELABLE_STATUSES: EnglishAssessmentStatus[] = ['requested', 'notified'];

export function isActive(status: EnglishAssessmentStatus): boolean {
  return ACTIVE_STATUSES.includes(status);
}

/** La última llamada fue corta: el candidato tiene que volver a llamar. */
export function lastCallWasShort(assessment: Pick<EnglishAssessmentBase, 'status' | 'calls'>): boolean {
  if (assessment.status !== 'notified') return false;
  const last = assessment.calls[assessment.calls.length - 1];
  return last?.verdict === 'insufficient' || last?.verdict === 'lost';
}

// ─── Teléfono ────────────────────────────────────────────────────────────────

/** Los indicativos de quienes usan TBit Jobs. Orden: los más comunes primero. */
export const PHONE_COUNTRIES: Array<{ iso: string; dial: string; name: string }> = [
  { iso: 'CO', dial: '57', name: 'Colombia' },
  { iso: 'MX', dial: '52', name: 'México' },
  { iso: 'GT', dial: '502', name: 'Guatemala' },
  { iso: 'HN', dial: '504', name: 'Honduras' },
  { iso: 'SV', dial: '503', name: 'El Salvador' },
  { iso: 'NI', dial: '505', name: 'Nicaragua' },
  { iso: 'CR', dial: '506', name: 'Costa Rica' },
  { iso: 'PA', dial: '507', name: 'Panamá' },
  { iso: 'VE', dial: '58', name: 'Venezuela' },
  { iso: 'EC', dial: '593', name: 'Ecuador' },
  { iso: 'PE', dial: '51', name: 'Perú' },
  { iso: 'BO', dial: '591', name: 'Bolivia' },
  { iso: 'CL', dial: '56', name: 'Chile' },
  { iso: 'AR', dial: '54', name: 'Argentina' },
  { iso: 'PY', dial: '595', name: 'Paraguay' },
  { iso: 'UY', dial: '598', name: 'Uruguay' },
  { iso: 'BR', dial: '55', name: 'Brasil' },
  { iso: 'DO', dial: '1', name: 'Rep. Dominicana / EE. UU. / Canadá' },
  { iso: 'ES', dial: '34', name: 'España' },
];

/** '+57' + '300 123 4567' → '+573001234567', o null si no es un E.164 posible. */
export function composeE164(dial: string, local: string): string | null {
  const digits = local.replace(/\D+/g, '').replace(/^0+/, '');
  const dialDigits = dial.replace(/\D+/g, '');
  if (!digits || !dialDigits) return null;
  // Quien pegó el número con el indicativo adelante no lo duplica. Más largo
  // que un número nacional = trae el indicativo. Brasil tiene celulares de 11
  // dígitos y un código de área 55 (+55 55 9…): ahí el tope es 11, o se comería
  // el código de área.
  const nationalMax = dialDigits === '55' ? 11 : 10;
  const national = digits.startsWith(dialDigits) && digits.length > nationalMax ? digits.slice(dialDigits.length) : digits;
  const full = `+${dialDigits}${national}`;
  return /^\+[1-9]\d{7,14}$/.test(full) ? full : null;
}

/** '+573001234567' → { dial: '57', local: '3001234567' } por el indicativo más largo que calce. */
export function splitE164(e164: string | null | undefined): { dial: string; local: string } {
  const digits = String(e164 ?? '').replace(/\D+/g, '');
  if (!digits) return { dial: PHONE_COUNTRIES[0].dial, local: '' };
  const match = [...PHONE_COUNTRIES]
    .sort((a, b) => b.dial.length - a.dial.length)
    .find((country) => digits.startsWith(country.dial));
  if (!match) return { dial: PHONE_COUNTRIES[0].dial, local: digits };
  return { dial: match.dial, local: digits.slice(match.dial.length) };
}

/** '+573001234567' → '+57 300 123 4567' para mostrar. */
export function formatE164(e164: string | null | undefined): string {
  if (!e164) return '';
  const { dial, local } = splitE164(e164);
  const groups = local.length === 10 ? [local.slice(0, 3), local.slice(3, 6), local.slice(6)] : [local];
  return `+${dial} ${groups.join(' ')}`;
}
